import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { CustomDropdownContainer, FormFieldLabel, PageHeader } from "CommonComponent";
import { EmployeeDropdown } from "CommonDropdowns";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  FREQUENCY_OPTIONS,
  fetchPolicies,
  getErrorMessage,
  simulateGeneration,
} from "../pafEngineApi";
import "../PafEngine.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const MONTH_OPTIONS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
].map((m) => ({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) }));

const PafEngineSimulation = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [employee, setEmployee] = useState(null);
  const [month, setMonth] = useState(
    MONTH_OPTIONS.find((m) => m.value === "august") || MONTH_OPTIONS[0]
  );
  const [frequency, setFrequency] = useState(null);
  const [policyId, setPolicyId] = useState(null);
  const [policyOptions, setPolicyOptions] = useState([]);
  const [result, setResult] = useState(null);

  const loadPolicies = useCallback(async () => {
    try {
      const policies = await fetchPolicies(objectViewedId);
      setPolicyOptions([
        { value: "", label: "Auto Detect" },
        ...policies.map((p) => ({
          value: p.id,
          label: `${p.appraisal_type?.name || "Type"} · ${p.frequency}`,
        })),
      ]);
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const run = async () => {
    if (!employee?.value) {
      dispatch(notify("Select an employee."));
      return;
    }
    dispatch(showCommonLoader());
    try {
      const payload = await simulateGeneration(
        {
          employee_id: employee.value,
          month: month?.value,
          frequency: frequency?.value || undefined,
          policy_id: policyId?.value || undefined,
        },
        objectViewedId
      );
      setResult(payload);
    } catch (error) {
      setResult(null);
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  return (
    <div className="paf-settings-page">
      <PageHeader title="PAF Preview" />
      <div className="main-content-div page-card">
        <p className="paf-engine-tagline">
          See if an employee would get a PAF — without creating one.
        </p>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <EmployeeDropdown
              label="Employee"
              value={employee}
              onChange={setEmployee}
              required
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Month</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={MONTH_OPTIONS}
              value={month}
              onChange={setMonth}
              components={{ ValueContainer: CustomDropdownContainer }}
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Frequency (optional)</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={FREQUENCY_OPTIONS}
              value={frequency}
              onChange={setFrequency}
              isClearable
              components={{ ValueContainer: CustomDropdownContainer }}
            />
          </Col>
          <Col md={2}>
            <FormFieldLabel>Policy</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={policyOptions}
              value={policyId}
              onChange={setPolicyId}
              components={{ ValueContainer: CustomDropdownContainer }}
            />
          </Col>
        </Row>
        <Button className="n-u-btn mb-3" onClick={run}>
          Check Employee
        </Button>

        {result ? (
          <div className="paf-engine-result-box">
            <div>
              <strong>Employee:</strong> {result.employee?.name}
            </div>
            <div>
              <strong>Employment Status:</strong> {result.employee?.employment_status || "—"}
            </div>
            <div>
              <strong>Policy:</strong>{" "}
              {result.policy
                ? `${result.policy.appraisal_type} · ${result.policy.frequency}`
                : "Not matched"}
            </div>
            <div>
              <strong>Eligible:</strong>{" "}
              <span className={result.eligible ? "ok" : "no"}>
                {result.eligible ? "Yes" : "No"}
              </span>
            </div>
            <div>
              <strong>Reason:</strong> {result.reason}
            </div>
            <div>
              <strong>Reporting To:</strong> {result.reporting_to || "—"}
            </div>
            <div>
              <strong>Frequency:</strong> {result.frequency || "—"}
            </div>
            <div>
              <strong>Would Generate:</strong>{" "}
              <span className={result.would_generate ? "ok" : "no"}>
                {result.would_generate ? "Yes" : "No"}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PafEngineSimulation;
